import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@src/components';
import { OrderVoucherType } from '@src/generated/graphql';
import { VOUCHER_CONFIGS } from '@src/pages/orders/constants/vouchers';

interface Props {
  className?: string;
  quantity: number;
  type: OrderVoucherType;
}

interface OrderVoucherTypeConfig {
  text: JSX.Element;
}

function useOrderVoucherTypeConfig(
  type: OrderVoucherType,
  count: number,
): OrderVoucherTypeConfig {
  const { t } = useTranslation();
  switch (type) {
    case 'NIF':
      return {
        text: (
          <Trans
            t={t}
            i18nKey="translation:dashboard.vouchers.nif"
            defaultValue="<strong>{{count}}</strong> pending Portugal NIF orders."
            count={count}
          />
        ),
      };
    case 'IN_PERSON_BANK':
      return {
        text: (
          <Trans
            t={t}
            i18nKey="translation:dashboard.vouchers.in_person_bank"
            defaultValue="<strong>{{count}}</strong> pending In-Person Bank Account Setup orders."
            count={count}
          />
        ),
      };
    case 'REMOTE_BANK':
      return {
        text: (
          <Trans
            t={t}
            i18nKey="translation:dashboard.vouchers.remote_bank"
            defaultValue="<strong>{{count}}</strong> pending Remote Bank Account Setup orders."
            count={count}
          />
        ),
      };
    default:
      throw new Error(`Invalid order type: ${type as string}`);
  }
}

function VoucherItem({ className, quantity, type }: Props): JSX.Element {
  const config = useOrderVoucherTypeConfig(type, quantity);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const voucherConfig = VOUCHER_CONFIGS[type];

  return (
    <div
      className={clsx(
        'justify-between border-l-4 bg-white p-4',
        voucherConfig.accentClass,
        className,
      )}
    >
      <div className="flex flex-col justify-between space-y-4 md:flex-row md:space-x-4 md:space-y-0">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <voucherConfig.icon
              className={clsx('h-6 w-6', voucherConfig.iconClass)}
              aria-hidden="true"
            />
          </div>

          <div className="ml-3">
            <p className="text-sm">
              {config.text}{' '}
              {t(
                'dashboard.vouchers.provide_info',
                'Provide your documents and information to get the process started.',
              )}
            </p>
          </div>
        </div>
        <Button
          onClick={() => {
            void navigate(voucherConfig.intakeUrl);
          }}
          className="flex-shrink-0"
        >
          {t('dashboard.vouchers.start_intake_button', 'Start intake')}
        </Button>
      </div>
    </div>
  );
}

export default VoucherItem;
